import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, X, Camera, Loader, ExternalLink, Save, RefreshCw, FileText, Trash2, Undo2,
  AlertTriangle, Pencil, Paperclip, CreditCard, Receipt, Bell, BellRing, CalendarPlus,
} from 'lucide-react';
import {
  api, DOC_URL, EASYPAY_URL, BPAY_BILLER, fmtDate, fmtDay, fmtMoney, fileToBase64, checkFile, copyText,
  labelStyle, inputStyle, th, td, num, GREEN, AMBER, RED,
} from '../utils/atoShared';
import { cents, dollars, todayIso } from '../utils/atoPlan';
import {
  TAX_KINDS, DOC_TYPES, fyOf, fyLabel, workTaxItem, taxSummary, taxYears, matchTaxItem, suggestTaxItem, itemFromExtract, nextReminder,
} from '../utils/atoTax';

// ── Tax paid ────────────────────────────────────────────────────────────────────
// Every tax payment the businesses (and Paul and Holly) make outside a payment plan: the
// BAS each quarter, IAS, PAYG instalments, a notice of assessment, a reminder notice.
// Drop the ATO's document → Claude reads who, which period, how much and by when. Drop
// the bank receipt → it's recorded against it. The summary answers "how much tax did
// each company pay this financial year, and what's still owing".
//
// "Paid in a year" is by PAYMENT date (cash out the door in that FY), not by the period
// the statement covers. Nothing here is ever deleted: Remove hides a record (Restore
// brings it back), and every file stays in the bucket.

const STATUS = {
  paid: { label: 'Paid', color: GREEN },
  late: { label: 'Paid late', color: AMBER },
  part: { label: 'Part-paid', color: AMBER },
  overdue: { label: 'Overdue', color: RED },
  due: { label: 'To pay', color: 'var(--hp-gold)' },
  refund: { label: 'Refund', color: GREEN },
  nil: { label: 'Nil', color: 'var(--hp-dim)' },
};
const Pill = ({ s }) => {
  const x = STATUS[s] || STATUS.nil;
  return <span style={{ fontSize: '.7rem', fontWeight: 700, color: x.color, border: `1px solid ${x.color}`, borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' }}>{x.label}</span>;
};
const itemTitle = (i) => [i.entity, i.kind, i.period].filter(Boolean).join(' · ');
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const BREAKDOWN = [['gst', 'GST'], ['paygw', 'PAYG withheld'], ['paygi', 'PAYG instalment'], ['fbt', 'FBT'], ['incomeTax', 'Income tax'], ['gic', 'GIC'], ['previous', 'Earlier overdue'], ['other', 'Other']];

const drawerWrap = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' };
const drawerBody = (w) => ({ width: w, maxWidth: '100%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 24 });
const dropZone = { border: '2px dashed var(--hp-border)', borderRadius: 10, padding: 14, textAlign: 'center' };
const kicker = { fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 };

// Open EasyPay with the PRN on the clipboard (both must START inside the click — see
// payOnEasyPay on the plans tab).
async function payItem(item, w, notify) {
  const copied = item.prn ? copyText(item.prn) : Promise.resolve(false);
  window.open(EASYPAY_URL, '_blank', 'noopener');
  if (item.prn && (await copied)) notify?.(`PRN ${item.prn} copied — paste it into EasyPay, amount ${fmtMoney(w.owing)}`, 'success');
  else if (!item.prn) notify?.('No PRN recorded on this one — EasyPay needs it. It is on the statement.', 'info');
}

// ISO date ± days (local calendar day, no time zone drift).
const addDays = (iso, n) => {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// A calendar file for the same reminder — 9am that day with an alert, so it also shows up in
// Apple / Google Calendar. Opening it is the phone's or Mac's own "Add to calendar".
function downloadIcs(item, w, date, note) {
  const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');
  const d = date.replace(/-/g, '');
  const esc = (t) => String(t || '').replace(/\\/g, '\\\\').replace(/([,;])/g, '\\$1').replace(/\n/g, '\\n');
  const what = `${item.entity} ${[item.kind, item.period].filter(Boolean).join(' ')}`.trim();
  const desc = [
    `${fmtMoney(w.owing)} to the ATO`,
    item.dueDate ? `Due ${fmtDate(item.dueDate)} · pay by ${fmtDate(w.payBy)}` : '',
    item.prn ? `BPAY biller ${BPAY_BILLER} · ref (PRN) ${item.prn}` : '',
    note || '',
  ].filter(Boolean).join('\n');
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//HP Engine//ATO reminder//EN', 'BEGIN:VEVENT',
    `UID:atotax-${item.id}-${d}@hollypaul.com`, `DTSTAMP:${stamp}`,
    `DTSTART;TZID=Australia/Sydney:${d}T090000`, `DTEND;TZID=Australia/Sydney:${d}T091500`,
    `SUMMARY:${esc(`Pay the ATO: ${what} ${fmtMoney(w.owing)}`)}`, `DESCRIPTION:${esc(desc)}`,
    'BEGIN:VALARM', 'ACTION:DISPLAY', 'TRIGGER:PT0M', `DESCRIPTION:${esc(what)}`, 'END:VALARM',
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
  const a = document.createElement('a');
  a.href = url; a.download = `ATO reminder ${date}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// The bell: pick a day and HP Engine pushes you at 6am that morning (with the amount, due
// date and PRN). Not sent if it's paid by then. Optional calendar copy too.
function ReminderDialog({ item, w, notify, onClose, onSaved }) {
  const today = todayIso();
  const tomorrow = addDays(today, 1);
  const current = nextReminder(item);
  const picks = [
    ['Tomorrow', tomorrow],
    w.payBy && ['A week before pay-by', addDays(w.payBy, -7)],
    w.payBy && ['Day before pay-by', addDays(w.payBy, -1)],
    w.payBy && ['On pay-by', w.payBy],
  ].filter((p) => p && p[1] >= tomorrow).filter((p, i, a) => a.findIndex((q) => q[1] === p[1]) === i);
  const [date, setDate] = useState(current ? current.date : (picks[1] || picks[0])[1]);
  const [note, setNote] = useState(current ? current.note || '' : '');
  const [busy, setBusy] = useState(false);

  const send = async (payload, done) => {
    setBusy(true);
    try {
      const r = await api('setTaxReminder', { itemId: item.id, id: current ? current.id : '', ...payload });
      if (!r.success) { notify?.(r.error || 'Could not save the reminder', 'error'); return; }
      notify?.(done, 'success');
      onSaved(r.item);
    } catch (e) { notify?.(`Error: ${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ ...drawerWrap, justifyContent: 'center', alignItems: 'center', padding: 16 }} onClick={onClose}>
      <div className="card" style={{ width: 420, maxWidth: '100%', padding: 20, background: 'var(--hp-bg)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <div style={kicker}>Set a reminder</div>
            <div style={{ fontWeight: 700 }}>{item.entity}</div>
            <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>
              {[item.kind, item.period].filter(Boolean).join(' · ')} · {fmtMoney(w.owing)}{item.dueDate ? ` · due ${fmtDay(item.dueDate)} · pay by ${fmtDay(w.payBy)}` : ''}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14} /></button>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '14px 0 10px' }}>
          {picks.map(([label, d]) => (
            <button key={d} className={`btn btn-sm ${date === d ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDate(d)}>{label}<span style={{ opacity: 0.7, marginLeft: 4 }}>{fmtDay(d)}</span></button>
          ))}
        </div>
        <label style={labelStyle}>Remind me on</label>
        <input type="date" className="input" style={inputStyle} min={tomorrow} value={date} onChange={(e) => setDate(e.target.value)} />
        <label style={{ ...labelStyle, marginTop: 10 }}>Note (optional)</label>
        <input className="input" style={inputStyle} maxLength={140} placeholder="e.g. move money across first" value={note} onChange={(e) => setNote(e.target.value)} />
        <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', margin: '10px 0 14px', lineHeight: 1.5 }}>
          HP Engine pushes your phone and Mac about 6am that day, with the amount, due date and PRN. It isn't sent if this is paid by then.
          The automatic reminders (a week before pay-by, on it, on the due date) still come as well.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn btn-primary" disabled={busy || !ISO.test(date) || date < tomorrow}
            onClick={() => send({ date, note }, `Reminder set for ${fmtDay(date)}`)}>
            {busy ? <Loader size={14} /> : <BellRing size={14} />} {current ? 'Update reminder' : 'Set reminder'}
          </button>
          <button className="btn btn-secondary" disabled={!ISO.test(date)} onClick={() => downloadIcs(item, w, date, note)} title="Downloads a calendar event (9am, with an alert). Open it to add it to Apple or Google Calendar.">
            <CalendarPlus size={14} /> Add to calendar
          </button>
          {current && <button className="btn btn-ghost" style={{ color: RED, marginLeft: 'auto' }} disabled={busy} onClick={() => send({ cancel: true }, 'Reminder cancelled')}>Cancel reminder</button>}
        </div>
      </div>
    </div>
  );
}

// The bell button: "Remind" until one is set, then the day it's set for.
function RemindButton({ item, w, onOpen, compact }) {
  if (!(w.owing > 0)) return null;
  const rem = nextReminder(item);
  const sent = rem && rem.date < todayIso();
  if (!rem) return <button className="btn btn-ghost btn-sm" onClick={onOpen} title="Set a reminder"><Bell size={12} />{compact ? '' : ' Remind'}</button>;
  return (
    <button className="btn btn-ghost btn-sm" onClick={onOpen} style={{ color: 'var(--hp-gold)' }}
      title={`${sent ? 'Reminded' : 'Reminder'} ${fmtDate(rem.date)}${rem.note ? ` — ${rem.note}` : ''}. Click to change.`}>
      <BellRing size={12} /> {fmtDay(rem.date)}
    </button>
  );
}

// The receipt fields, shared by the statement drawer and the payment drawer.
function ReceiptFields({ r, set }) {
  return (
    <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div><label style={labelStyle}>Paid on</label><input type="date" className="input" style={inputStyle} value={r.date} onChange={(e) => set('date', e.target.value)} /></div>
      <div><label style={labelStyle}>Amount to the ATO ($)</label><input type="number" step="0.01" className="input" style={inputStyle} value={r.amount} onChange={(e) => set('amount', e.target.value)} /></div>
      <div><label style={labelStyle}>Card fee ($) — not credited</label><input type="number" step="0.01" className="input" style={inputStyle} value={r.fee} onChange={(e) => set('fee', e.target.value)} placeholder="0.00 for BPAY" /></div>
      <div><label style={labelStyle}>Method</label>
        <select className="input" style={inputStyle} value={r.method} onChange={(e) => set('method', e.target.value)}>{['BPAY', 'Transfer', 'Card', 'Other'].map((t) => <option key={t}>{t}</option>)}</select></div>
      <div><label style={labelStyle}>Receipt no.</label><input className="input" style={inputStyle} value={r.receiptNo} onChange={(e) => set('receiptNo', e.target.value)} /></div>
      <div><label style={labelStyle}>Reference (PRN)</label><input className="input" style={inputStyle} value={r.reference} onChange={(e) => set('reference', e.target.value)} /></div>
    </div>
  );
}
const blankReceipt = () => ({ date: '', amount: '', fee: '', method: 'BPAY', reference: '', receiptNo: '', note: '' });
const receiptFromExtract = (x, cur) => ({
  ...cur,
  date: ISO.test(x.date) ? x.date : cur.date,
  amount: Number(x.amount) > 0 ? Number(x.amount) : cur.amount,
  fee: Number(x.fee) > 0 ? Number(x.fee) : '',
  method: ['BPAY', 'Transfer', 'Card', 'Other'].includes(x.method) ? x.method : cur.method,
  reference: x.reference || cur.reference,
  receiptNo: x.receiptNo || cur.receiptNo,
});

// ── Statement drawer: the ATO document + (optionally) the paid receipt, in one go ──
function TaxItemDrawer({ item, items, payments, knownEntities, onClose, onSaved, onOpenItem, onTogglePayment, notify }) {
  const editing = !!item;
  const [form, setForm] = useState(() => item ? { ...item } : {
    entity: '', abn: '', kind: 'BAS', docType: 'Activity statement', period: '', periodEnd: '', issued: '',
    amount: '', dueDate: '', prn: '', notes: '', breakdown: null,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [doc, setDoc] = useState(null);         // { base64, contentType, name, extracted }
  const [rcpt, setRcpt] = useState(null);       // { base64, contentType, name, extracted }
  const [r, setR] = useState(blankReceipt);
  const setRf = (k, v) => setR((x) => ({ ...x, [k]: v }));
  const [showReceipt, setShowReceipt] = useState(false);
  const [busy, setBusy] = useState('');
  const [saving, setSaving] = useState(false);
  const [match, setMatch] = useState(null);

  const readDoc = async (f) => {
    if (!checkFile(f, notify)) return;
    setBusy('doc');
    try {
      const { base64, contentType } = await fileToBase64(f);
      const res = await api('readTaxDoc', { base64, contentType });
      setDoc({ base64, contentType, name: f.name, extracted: res.success ? res.extracted : null });
      if (res.success) {
        const x = itemFromExtract(res.extracted);
        // A reminder about a statement already on file belongs on THAT record.
        const m = !editing ? matchTaxItem(items, x) : null;
        setMatch(m);
        setForm((cur) => ({
          ...cur,
          entity: cur.entity || x.entity, abn: cur.abn || x.abn,
          kind: x.kind || cur.kind, docType: x.docType || cur.docType,
          period: x.period || cur.period, periodEnd: x.periodEnd || cur.periodEnd, issued: x.issued || cur.issued,
          amount: x.amount !== '' ? x.amount : cur.amount, dueDate: x.dueDate || cur.dueDate,
          prn: x.prn || cur.prn, breakdown: x.breakdown || cur.breakdown, notes: cur.notes || x.notes,
        }));
        notify?.(`Read ${x.docType || 'the document'} — check it, then save`, 'success');
      } else notify?.(`${res.error || "Couldn't read it"}. Fill it in by hand — the file still saves.`, 'info');
    } catch (e) { notify?.(`Could not process the file: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const readRcpt = async (f) => {
    if (!checkFile(f, notify)) return;
    setBusy('rcpt');
    setShowReceipt(true);
    try {
      const { base64, contentType } = await fileToBase64(f);
      const res = await api('readReceipt', { base64, contentType });
      setRcpt({ base64, contentType, name: f.name, extracted: res.success ? res.extracted : null });
      if (res.success) {
        setR((cur) => receiptFromExtract(res.extracted, cur));
        notify?.('Receipt read — check the date and amount', 'success');
      } else notify?.(`${res.error || "Couldn't read the receipt"}. Enter the date and amount — the receipt still saves.`, 'info');
    } catch (e) { notify?.(`Could not process the file: ${e.message}`, 'error'); }
    finally { setBusy(''); }
  };

  const w = editing ? workTaxItem(item, payments, todayIso()) : null;
  const pays = editing ? payments.filter((p) => p.itemId === item.id).sort((a, b) => b.date.localeCompare(a.date)) : [];
  const refMismatch = form.prn && r.reference && r.reference.replace(/\D/g, '') && !r.reference.replace(/\D/g, '').includes(form.prn.replace(/\D/g, ''));
  const wantReceipt = showReceipt && (rcpt || Number(r.amount) > 0);

  const save = async () => {
    if (wantReceipt && (!ISO.test(r.date) || !(Number(r.amount) > 0))) { notify?.('The receipt needs a payment date and amount', 'error'); return; }
    setSaving(true);
    try {
      const res = await api('saveTaxItem', {
        item: form,
        docBase64: doc?.base64, docContentType: doc?.contentType, docName: doc?.name, docKind: form.docType, extracted: doc?.extracted,
        receipt: wantReceipt ? {
          ...r, extracted: rcpt?.extracted || null,
          fileBase64: rcpt?.base64, fileContentType: rcpt?.contentType,
          fileName: rcpt ? `${(form.entity || 'ATO').replace(/[^\w.\- ]/g, '_')} ${form.kind} payment ${r.date}` : undefined,
        } : null,
      });
      if (!res.success) { notify?.(res.error || 'Save failed', 'error'); if (res.item) onSaved(res.item, null); return; }
      const after = workTaxItem(res.item, payments.concat(res.payment ? [res.payment] : []), todayIso());
      notify?.(res.payment
        ? (after.owing > 0 ? `Payment recorded — ${fmtMoney(after.owing)} still owing on it` : 'Payment recorded — paid in full')
        : (editing ? 'Saved' : 'Statement added'), 'success');
      onSaved(res.item, res.payment);
    } catch (e) { notify?.(`Error: ${e.message}`, 'error'); }
    finally { setSaving(false); }
  };

  const matched = match && items.find((i) => i.id === match.itemId);

  return (
    <div style={drawerWrap} onClick={onClose}>
      <div style={drawerBody(620)} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={kicker}>Tax paid</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{editing ? itemTitle(item) : 'Add a statement or notice'}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
          <div style={dropZone} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); readDoc(e.dataTransfer?.files?.[0]); }}>
            <div style={{ color: 'var(--hp-dim)', fontSize: '.82rem', marginBottom: 8, minHeight: 34 }}>
              {busy === 'doc' ? <><Loader size={14} /> Reading…</>
                : doc ? <><FileText size={14} /> {doc.name}</>
                : editing ? 'Add another ATO document (e.g. the reminder notice)'
                : 'The activity statement, reminder notice or assessment'}
            </div>
            <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
              <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} disabled={!!busy}
                onChange={(e) => { readDoc(e.target.files?.[0]); e.target.value = ''; }} />
              <Paperclip size={13} /> {doc ? 'Different file' : 'ATO document'}
            </label>
          </div>
          <div style={dropZone} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); readRcpt(e.dataTransfer?.files?.[0]); }}>
            <div style={{ color: 'var(--hp-dim)', fontSize: '.82rem', marginBottom: 8, minHeight: 34 }}>
              {busy === 'rcpt' ? <><Loader size={14} /> Reading…</>
                : rcpt ? <><Receipt size={14} /> {rcpt.name}</>
                : 'The paid receipt (bank BPAY or EasyPay), if it’s paid'}
            </div>
            <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer' }}>
              <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} disabled={!!busy}
                onChange={(e) => { readRcpt(e.target.files?.[0]); e.target.value = ''; }} />
              <Camera size={13} /> {rcpt ? 'Different receipt' : 'Paid receipt'}
            </label>
          </div>
        </div>

        {matched && (
          <div className="card" style={{ padding: 12, marginBottom: 14, borderColor: AMBER, fontSize: '.84rem' }}>
            <AlertTriangle size={13} color={AMBER} /> This looks like <strong>{itemTitle(matched)}</strong>, already on file ({match.why}).
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => onOpenItem(matched, doc)}>Attach it to that one</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setMatch(null)}>No — it's a new one</button>
            </div>
          </div>
        )}

        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Who (entity)</label>
            <input className="input" style={inputStyle} list="ato-tax-entities" value={form.entity} onChange={(e) => set('entity', e.target.value)} placeholder="e.g. PH Bouveng Pty Ltd" />
            <datalist id="ato-tax-entities">{knownEntities.map((n) => <option key={n} value={n} />)}</datalist>
          </div>
          <div><label style={labelStyle}>ABN</label><input className="input" style={inputStyle} value={form.abn} onChange={(e) => set('abn', e.target.value)} /></div>
          <div><label style={labelStyle}>Tax</label>
            <select className="input" style={inputStyle} value={form.kind} onChange={(e) => set('kind', e.target.value)}>{TAX_KINDS.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label style={labelStyle}>Document</label>
            <select className="input" style={inputStyle} value={form.docType} onChange={(e) => set('docType', e.target.value)}>{DOC_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label style={labelStyle}>Period</label><input className="input" style={inputStyle} value={form.period} onChange={(e) => set('period', e.target.value)} placeholder="e.g. Jul–Sep 2026" /></div>
          <div><label style={labelStyle}>Period ends</label><input type="date" className="input" style={inputStyle} value={form.periodEnd} onChange={(e) => set('periodEnd', e.target.value)} /></div>
          <div><label style={labelStyle}>Amount payable ($)</label><input type="number" step="0.01" className="input" style={inputStyle} value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="negative = refund" /></div>
          <div><label style={labelStyle}>Due</label><input type="date" className="input" style={inputStyle} value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} /></div>
          <div><label style={labelStyle}>PRN</label><input className="input" style={inputStyle} value={form.prn} onChange={(e) => set('prn', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Notes</label><textarea className="input" style={{ ...inputStyle, minHeight: 50 }} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
        </div>
        {form.docType === 'Reminder notice' && (
          <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginTop: 8 }}>
            A reminder notice states the whole balance owing on the account — which can include an earlier statement and interest. If that statement is already on file, attach the notice to it instead of adding a second record, so the same tax isn't counted twice.
          </div>
        )}
        {form.breakdown && BREAKDOWN.some(([k]) => Number(form.breakdown[k])) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {BREAKDOWN.filter(([k]) => Number(form.breakdown[k])).map(([k, l]) => (
              <span key={k} style={{ fontSize: '.74rem', border: '1px solid var(--hp-border)', borderRadius: 6, padding: '2px 8px' }}>{l} <strong>{fmtMoney(form.breakdown[k])}</strong></span>
            ))}
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          {!showReceipt ? (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowReceipt(true)}><Plus size={12} /> It's paid — add the payment</button>
          ) : (
            <div className="card" style={{ padding: 14 }}>
              <div style={{ ...labelStyle, marginBottom: 10 }}>Payment {rcpt ? '(read off the receipt)' : ''}</div>
              <ReceiptFields r={r} set={setRf} />
              {Number(r.fee) > 0 && <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginTop: 6 }}>Card charged {fmtMoney(dollars(cents(r.amount) + cents(r.fee)))}. Only {fmtMoney(r.amount)} reaches the ATO; the fee is kept on record but not counted as tax paid.</div>}
              {refMismatch && <div style={{ fontSize: '.76rem', color: AMBER, marginTop: 6 }}><AlertTriangle size={12} /> The receipt's reference doesn't match this PRN ({form.prn}) — check it went to the right account.</div>}
            </div>
          )}
        </div>

        {editing && (
          <div style={{ marginTop: 20 }}>
            <div style={labelStyle}>On file</div>
            {(item.docs || []).length ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {item.docs.map((d) => <a key={d.id} className="btn btn-secondary btn-sm" href={`${DOC_URL}?tax=${item.id}&doc=${d.id}`} target="_blank" rel="noreferrer"><FileText size={12} /> {d.kind}: {d.name}</a>)}
              </div>
            ) : <div style={{ fontSize: '.82rem', color: 'var(--hp-dim)', marginBottom: 10 }}>No ATO document yet.</div>}
            {pays.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Paid</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th}>Ref</th><th style={th} /></tr></thead>
                <tbody>
                  {pays.map((p) => (
                    <tr key={p.id} style={{ opacity: p.deletedAt ? 0.45 : 1 }}>
                      <td style={td}>{fmtDate(p.date)}{p.ocrError ? <span title={p.ocrError} style={{ color: AMBER }}> ⚠</span> : ''}</td>
                      <td style={{ ...td, ...num, textDecoration: p.deletedAt ? 'line-through' : 'none' }}>{fmtMoney(p.amount)}{Number(p.fee) > 0 && <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>+ {fmtMoney(p.fee)} card fee</div>}</td>
                      <td style={{ ...td, fontSize: '.76rem', color: 'var(--hp-dim)' }}>{[p.method, p.receiptNo].filter(Boolean).join(' · ')}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {p.file && <a className="btn btn-ghost btn-sm" href={`${DOC_URL}?taxpay=${p.id}`} target="_blank" rel="noreferrer" title="View receipt"><ExternalLink size={12} /></a>}
                        {p.deletedAt
                          ? <button className="btn btn-ghost btn-sm" onClick={() => onTogglePayment(p, false)}><Undo2 size={12} /> Restore</button>
                          : <button className="btn btn-ghost btn-sm" style={{ color: RED }} onClick={() => onTogglePayment(p, true)} title="Remove"><Trash2 size={12} /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {w && <div style={{ fontSize: '.84rem', marginTop: 8 }}>Paid {fmtMoney(w.paid)} of {fmtMoney(w.amount)} · <Pill s={w.status} />{w.owing > 0 ? <> · <strong>{fmtMoney(w.owing)}</strong> owing</> : ''}</div>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving || !!busy}>
            {saving ? <><Loader size={14} /> Saving…</> : <><Save size={14} /> {editing ? 'Save' : wantReceipt ? 'Save statement + payment' : 'Save statement'}</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Payment drawer: just a receipt — it finds the statement it pays ───────────────
function TaxPaymentDrawer({ items, payments, defaultItemId, onClose, onSaved, notify }) {
  const today = todayIso();
  const open = items.filter((i) => !i.deletedAt);
  const [itemId, setItemId] = useState(defaultItemId || '');
  const [r, setR] = useState(blankReceipt);
  const setRf = (k, v) => setR((x) => ({ ...x, [k]: v }));
  const [file, setFile] = useState(null);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const read = async (f) => {
    if (!checkFile(f, notify)) return;
    setBusy(true);
    try {
      const { base64, contentType } = await fileToBase64(f);
      const res = await api('readReceipt', { base64, contentType });
      setFile({ base64, contentType, name: f.name, extracted: res.success ? res.extracted : null });
      if (res.success) {
        setR((cur) => receiptFromExtract(res.extracted, cur));
        if (!itemId) {
          const sug = suggestTaxItem(items, payments, res.extracted, today);
          if (sug) { setItemId(sug.itemId); setWhy(sug.why); }
        }
        notify?.('Receipt read — check it, then save', 'success');
      } else notify?.(`${res.error || "Couldn't read the receipt"}. Enter the date and amount — the receipt still saves.`, 'info');
    } catch (e) { notify?.(`Could not process the file: ${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  const item = items.find((i) => i.id === itemId);
  const save = async () => {
    setSaving(true);
    try {
      const res = await api('saveTaxPayment', {
        payment: { ...r, itemId, extracted: file?.extracted || null },
        fileBase64: file?.base64, fileContentType: file?.contentType,
        fileName: file ? `${(item?.entity || 'ATO').replace(/[^\w.\- ]/g, '_')} ${item?.kind || 'tax'} payment ${r.date}` : undefined,
      });
      if (!res.success) { notify?.(res.error || 'Save failed', 'error'); return; }
      const after = item ? workTaxItem(item, payments.concat([res.payment]), today) : null;
      notify?.(after && after.owing > 0 ? `Recorded — ${fmtMoney(after.owing)} still owing on ${itemTitle(item)}` : 'Payment recorded', 'success');
      onSaved(res.payment);
    } catch (e) { notify?.(`Error: ${e.message}`, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={drawerWrap} onClick={onClose}>
      <div style={drawerBody(520)} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div><div style={kicker}>Tax paid</div><h3 style={{ margin: 0, fontSize: '1.1rem' }}>Record a payment</h3></div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ ...dropZone, marginBottom: 18 }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); read(e.dataTransfer?.files?.[0]); }}>
          <div style={{ color: 'var(--hp-dim)', fontSize: '.84rem', marginBottom: 8 }}>
            {busy ? <><Loader size={14} /> Reading the receipt…</> : file ? <><Receipt size={14} /> {file.name}</> : 'Upload the bank receipt — the date, amount and PRN are read off it and matched to the statement.'}
          </div>
          <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer' }}>
            <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} disabled={busy} onChange={(e) => { read(e.target.files?.[0]); e.target.value = ''; }} />
            <Camera size={13} /> {file ? 'Different receipt' : 'Upload receipt'}
          </label>
        </div>
        <label style={labelStyle}>Pays which statement</label>
        <select className="input" style={inputStyle} value={itemId} onChange={(e) => { setItemId(e.target.value); setWhy(''); }}>
          <option value="">— pick one —</option>
          {open.map((i) => { const w = workTaxItem(i, payments, today); return <option key={i.id} value={i.id}>{itemTitle(i)}{w.owing > 0 ? ` — ${fmtMoney(w.owing)} owing` : ''}</option>; })}
        </select>
        {why && <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', margin: '4px 0 0' }}>Picked by {why} — change it if that's wrong.</div>}
        {!open.length && <div style={{ fontSize: '.8rem', color: AMBER, marginTop: 6 }}>Add the statement first (or use “Add statement” and drop the receipt there).</div>}
        <div style={{ marginTop: 14 }}><ReceiptFields r={r} set={setRf} /></div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving || busy || !itemId}>{saving ? <><Loader size={14} /> Saving…</> : <><Save size={14} /> Save payment</>}</button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── The tab ─────────────────────────────────────────────────────────────────────
export default function TaxPaidTab({ notify, knownEntities = [] }) {
  const today = todayIso();
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fy, setFy] = useState(fyOf(today));
  const [entity, setEntity] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);
  const [itemDrawer, setItemDrawer] = useState(null); // { item }
  const [payDrawer, setPayDrawer] = useState(null);   // { itemId }
  const [remindFor, setRemindFor] = useState(null);   // item id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('loadTax');
      if (r.success) { setItems(r.items); setPayments(r.payments); }
      else notify?.(`Could not load: ${r.error || 'unknown error'}`, 'error');
    } catch (e) { notify?.(`Error: ${e.message}`, 'error'); }
    finally { setLoading(false); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const years = useMemo(() => {
    const ys = taxYears(items, payments);
    return ys.includes(fyOf(today)) ? ys : [fyOf(today)].concat(ys);
  }, [items, payments, today]);
  const summary = useMemo(() => taxSummary(items, payments, fy, today), [items, payments, fy, today]);
  const entities = Array.from(new Set(knownEntities.concat(items.filter((i) => !i.deletedAt).map((i) => i.entity)).filter(Boolean))).sort();

  const worked = useMemo(() => Object.fromEntries(items.map((i) => [i.id, workTaxItem(i, payments, today)])), [items, payments, today]);
  // What's owing is shown whatever year is picked — a debt doesn't belong to a filter.
  const owing = items.filter((i) => !i.deletedAt && worked[i.id].owing > 0 && (!entity || i.entity === entity))
    .sort((a, b) => (a.dueDate || '9').localeCompare(b.dueDate || '9'));
  // The list: items due in the year, or with a payment in the year.
  const inYear = (i) => !fy || fyOf(i.dueDate || i.periodEnd || i.issued) === fy
    || payments.some((p) => p.itemId === i.id && !p.deletedAt && fyOf(p.date) === fy);
  const list = items.filter((i) => (showRemoved || !i.deletedAt) && (!entity || i.entity === entity) && inYear(i))
    .sort((a, b) => (b.dueDate || b.periodEnd || '').localeCompare(a.dueDate || a.periodEnd || ''));
  const removedCount = items.filter((i) => i.deletedAt).length;
  const rows = entity ? summary.rows.filter((r) => r.entity === entity) : summary.rows;

  const mergeItem = (it) => setItems((xs) => (xs.some((x) => x.id === it.id) ? xs.map((x) => (x.id === it.id ? it : x)) : xs.concat([it])));
  const mergePay = (p) => setPayments((xs) => (xs.some((x) => x.id === p.id) ? xs.map((x) => (x.id === p.id ? p : x)) : xs.concat([p])));

  const toggle = async (what, rec, deleted) => {
    const r = await api('setTaxDeleted', { what, id: rec.id, deleted });
    if (!r.success) { notify?.(r.error || 'Failed', 'error'); return; }
    if (what === 'item') mergeItem(r.record); else mergePay(r.record);
    notify?.(deleted ? 'Removed — tick “Show removed” to restore it' : 'Restored', 'success');
  };
  // "Attach it to that one": the document just read goes onto the existing record.
  const attachTo = async (it, doc) => {
    setItemDrawer(null);
    if (doc) {
      const r = await api('addTaxDoc', { itemId: it.id, fileBase64: doc.base64, fileContentType: doc.contentType, fileName: doc.name, fileKind: (doc.extracted && doc.extracted.docType) || 'Document' });
      if (r.success) { mergeItem(r.item); notify?.('Attached', 'success'); setItemDrawer({ item: r.item }); return; }
      notify?.(r.error || 'Attach failed', 'error');
    }
    setItemDrawer({ item: it });
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <select className="input" style={{ ...inputStyle, width: 'auto' }} value={fy} onChange={(e) => setFy(Number(e.target.value))}>
          {years.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
          <option value={0}>All years</option>
        </select>
        <select className="input" style={{ ...inputStyle, width: 'auto', maxWidth: 240 }} value={entity} onChange={(e) => setEntity(e.target.value)}>
          <option value="">Every entity</option>
          {entities.map((n) => <option key={n}>{n}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
        <button className="btn btn-secondary" onClick={() => setItemDrawer({ item: null })}><Plus size={14} /> Add statement</button>
        <button className="btn btn-primary" onClick={() => setPayDrawer({ itemId: '' })} disabled={!items.some((i) => !i.deletedAt)}><Camera size={14} /> Record payment</button>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 24, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
      ) : !items.length ? (
        <div className="card" style={{ padding: 24, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
          Nothing recorded yet. <strong>Add statement</strong> and drop in a BAS, a reminder notice or a notice of assessment — and the bank receipt if it's paid.
          Claude reads who it's for, the period, the amount and the due date; the receipt gives the date paid.
        </div>
      ) : (
        <>
          <div className="card" style={{ padding: 14, marginBottom: 16, overflowX: 'auto' }}>
            <div style={{ ...labelStyle, marginBottom: 8 }}>Tax paid {fy ? `in ${fyLabel(fy)}` : '— all years'} (by payment date)</div>
            {!rows.length ? <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)' }}>No payments in this year.</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                <thead><tr><th style={th}>Entity</th><th style={th}>By tax</th><th style={{ ...th, textAlign: 'right' }}>Paid</th><th style={{ ...th, textAlign: 'right' }}>Owing now</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.entity}>
                      <td style={{ ...td, fontWeight: 700 }}>{r.entity}</td>
                      <td style={{ ...td, fontSize: '.76rem', color: 'var(--hp-dim)' }}>{Object.keys(r.byKind).map((k) => `${k} ${fmtMoney(r.byKind[k])}`).join(' · ') || '—'}</td>
                      <td style={{ ...td, ...num, fontWeight: 700 }}>{fmtMoney(r.paid)}</td>
                      <td style={{ ...td, ...num, color: r.overdue > 0 ? RED : r.owing > 0 ? 'var(--hp-text)' : 'var(--hp-dim)' }}>{r.owing > 0 ? fmtMoney(r.owing) : '—'}</td>
                    </tr>
                  ))}
                  {rows.length > 1 && (
                    <tr>
                      <td style={{ ...td, fontWeight: 800 }} colSpan={2}>Total</td>
                      <td style={{ ...td, ...num, fontWeight: 800 }}>{fmtMoney(dollars(rows.reduce((a, r) => a + cents(r.paid), 0)))}</td>
                      <td style={{ ...td, ...num, fontWeight: 800 }}>{fmtMoney(dollars(rows.reduce((a, r) => a + cents(r.owing), 0)))}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {owing.length > 0 && (
            <div className="card" style={{ padding: 14, marginBottom: 16 }}>
              <div style={{ ...labelStyle, marginBottom: 8 }}>To pay</div>
              {owing.map((i) => {
                const w = worked[i.id];
                return (
                  <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: '1px solid var(--hp-border)', fontSize: '.86rem', flexWrap: 'wrap' }}>
                    <span>
                      <strong>{i.entity}</strong> <span style={{ color: 'var(--hp-dim)' }}>· {[i.kind, i.period].filter(Boolean).join(' ')}{i.dueDate ? ` · due ${fmtDay(i.dueDate)} · pay by ${fmtDay(w.payBy)}` : ''}</span>
                      {i.prn && <span style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', fontSize: '.78rem' }}> · PRN {i.prn}</span>}
                    </span>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong style={num}>{fmtMoney(w.owing)}</strong> <Pill s={w.status} />
                      <RemindButton item={i} w={w} onOpen={() => setRemindFor(i.id)} />
                      <button className="btn btn-ghost btn-sm" onClick={() => payItem(i, w, notify)} title={`Opens EasyPay and copies the PRN (or BPAY biller ${BPAY_BILLER}, ref = PRN)`}><CreditCard size={12} /> EasyPay</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setPayDrawer({ itemId: i.id })}><Receipt size={12} /> Paid</button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="card" style={{ padding: 14, overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={labelStyle}>Statements & notices {fy ? `— ${fyLabel(fy)}` : ''}</span>
              {removedCount > 0 && <label style={{ fontSize: '.74rem', color: 'var(--hp-dim)' }}><input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} /> Show removed ({removedCount})</label>}
            </div>
            {!list.length ? <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)', padding: '8px 0' }}>Nothing in this year.</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead><tr>
                  <th style={th}>Entity</th><th style={th}>Tax · period</th><th style={th}>Due</th>
                  <th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={{ ...th, textAlign: 'right' }}>Paid</th><th style={th} /><th style={th} />
                </tr></thead>
                <tbody>
                  {list.map((i) => {
                    const w = worked[i.id];
                    return (
                      <tr key={i.id} style={{ opacity: i.deletedAt ? 0.45 : 1, cursor: 'pointer' }} onClick={() => !i.deletedAt && setItemDrawer({ item: i })}>
                        <td style={{ ...td, fontWeight: 600 }}>{i.entity}</td>
                        <td style={td}>{[i.kind, i.period].filter(Boolean).join(' · ')}<div style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>{i.docType}{(i.docs || []).length > 1 ? ` + ${i.docs.length - 1} more` : ''}</div></td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(i.dueDate)}</td>
                        <td style={{ ...td, ...num }}>{fmtMoney(i.amount)}</td>
                        <td style={{ ...td, ...num }}>{w.paid ? fmtMoney(w.paid) : '—'}{w.payments[0] && <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>{fmtDay(w.payments[w.payments.length - 1].date)}</div>}</td>
                        <td style={td}><Pill s={w.status} /></td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          {(i.docs || []).slice(0, 1).map((d) => <a key={d.id} className="btn btn-ghost btn-sm" href={`${DOC_URL}?tax=${i.id}&doc=${d.id}`} target="_blank" rel="noreferrer" title={`${d.kind}: ${d.name}`}><FileText size={12} /></a>)}
                          {w.payments.filter((p) => p.file).slice(-1).map((p) => <a key={p.id} className="btn btn-ghost btn-sm" href={`${DOC_URL}?taxpay=${p.id}`} target="_blank" rel="noreferrer" title="Receipt"><Receipt size={12} /></a>)}
                          {i.deletedAt
                            ? <button className="btn btn-ghost btn-sm" onClick={() => toggle('item', i, false)}><Undo2 size={12} /> Restore</button>
                            : <>
                              <RemindButton item={i} w={w} compact onOpen={() => setRemindFor(i.id)} />
                              <button className="btn btn-ghost btn-sm" onClick={() => setItemDrawer({ item: i })} title="Open"><Pencil size={12} /></button>
                              <button className="btn btn-ghost btn-sm" style={{ color: RED }} onClick={() => toggle('item', i, true)} title="Remove"><Trash2 size={12} /></button>
                            </>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 14, lineHeight: 1.5 }}>
              “Paid in a year” counts what left the bank in that financial year (1 Jul – 30 Jun) — the June-quarter BAS paid in July lands in the next year. Card fees are recorded but not counted as tax.
              Pay by BPAY (biller {BPAY_BILLER}, reference = the statement's PRN) or on EasyPay. Anything owing with a due date gets the same HP Engine reminders as the plans, and the bell sets one of your own for any day you pick. Every statement and receipt is kept: Remove hides a record and Restore brings it back.
            </div>
          </div>
        </>
      )}

      {itemDrawer && (
        <TaxItemDrawer key={itemDrawer.item ? itemDrawer.item.id : 'new'} item={itemDrawer.item} items={items} payments={payments} knownEntities={entities} notify={notify}
          onClose={() => setItemDrawer(null)}
          onOpenItem={attachTo}
          onTogglePayment={(p, d) => toggle('payment', p, d)}
          onSaved={(it, pay) => { mergeItem(it); if (pay) mergePay(pay); setItemDrawer(null); }} />
      )}
      {remindFor && items.some((x) => x.id === remindFor) && (
        <ReminderDialog item={items.find((x) => x.id === remindFor)} w={worked[remindFor]} notify={notify}
          onClose={() => setRemindFor(null)}
          onSaved={(it) => { mergeItem(it); setRemindFor(null); }} />
      )}
      {payDrawer && (
        <TaxPaymentDrawer items={items} payments={payments} defaultItemId={payDrawer.itemId} notify={notify}
          onClose={() => setPayDrawer(null)}
          onSaved={(p) => { mergePay(p); setPayDrawer(null); }} />
      )}
    </>
  );
}
